// Status (24h stories) and Channels.
import { serverOrigin } from "../platform";
import { signal } from "@preact/signals";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { api, getSession, type Channel } from "../api";
import { prepareFile } from "../media";
import * as S from "../state";
import { listTime, timeOf } from "../util";
import { Avatar, uploadAvatar } from "./Avatar";
import { ContactPicker } from "./Groups";
import { Icon } from "./Icon";
import { drawer, engine, infoOpen, openChat, toast } from "./Main";
import { Confirm } from "./Panels";
import { QUICK_REACTIONS } from "./Emoji";
import type { Msg } from "../db";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

function useEscape(fn: () => void) {
  useLayoutEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && fn();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fn]);
}

/** Open status viewer: whose updates, starting where. */
export const statusViewer = signal<{ owner: string; index: number } | null>(null);
/** Status composer: a text status, or a picked photo/video. */
export const statusComposer = signal<"text" | File | null>(null);

export const STATUS_BGS = ["#128c7e", "#34b7f1", "#8e44ad", "#c0392b", "#d35400", "#2c3e50", "#16a085", "#7f8c8d"];
const FONTS = ["system-ui, sans-serif", "Georgia, serif", "'Courier New', monospace", "'Comic Sans MS', 'Chalkboard SE', cursive"];

function ago(ts: number) {
  const m = Math.round((Date.now() - ts) / 60_000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const today = new Date().toDateString() === new Date(ts).toDateString();
  return (today ? "Today, " : "Yesterday, ") + timeOf(ts);
}

/** Avatar with the WhatsApp status ring: one arc per update, green = unseen. */
function StatusRing({ jid, name, url, list, size = 48 }: { jid: string; name: string; url?: string | null; list: S.StatusView[]; size?: number }) {
  const n = Math.max(1, list.length);
  // The ring sits 4px outside the avatar; with a 2.5px stroke the radius
  // must leave room for half of it, or the edge gets clipped.
  const r = size / 2 + 2.5;
  const c = 2 * Math.PI * r;
  const gap = n > 1 ? 4 : 0;
  const seg = c / n - gap;
  return (
    // (Preact 11 doesn't add "px" to numbers: a bare number here was dropped,
    // the box stayed avatar-sized and the ring hung off its corner.)
    <span class="status-ring" style={{ width: `${size + 8}px`, height: `${size + 8}px` }}>
      <svg width={size + 8} height={size + 8} viewBox={`0 0 ${size + 8} ${size + 8}`} aria-hidden="true">
        {list.map((s, i) => (
          <circle
            key={s.id}
            cx={size / 2 + 4}
            cy={size / 2 + 4}
            r={r}
            fill="none"
            stroke={s.viewed ? "var(--tick)" : "var(--badge)"}
            stroke-width="2.5"
            stroke-dasharray={`${seg} ${c - seg}`}
            stroke-dashoffset={-(i * (seg + gap)) + c / 4}
          />
        ))}
      </svg>
      <Avatar url={url} jid={jid} name={name} size={size} />
    </span>
  );
}

// ---- Status tab ---------------------------------------------------------------------------

export function StatusTab() {
  const me = S.myJid.value;
  const mine = S.myStatuses.value.filter((s) => s.expiresAt > Date.now());
  const others = [...S.statuses.value.entries()]
    .map(([owner, list]) => ({ owner, list: list.filter((s) => s.expiresAt > Date.now()) }))
    .filter((x) => x.list.length)
    .sort((a, b) => b.list[b.list.length - 1].createdAt - a.list[a.list.length - 1].createdAt);
  const recent = others.filter((x) => x.list.some((s) => !s.viewed));
  const viewed = others.filter((x) => x.list.every((s) => s.viewed));
  // Your own profile isn't in the contacts' profiles: it's the session's.
  const myProfile = S.profiles.value.get(me) ?? getSession()?.user;
  const file = useRef<HTMLInputElement>(null);

  const row = (x: { owner: string; list: S.StatusView[] }) => (
    <li key={x.owner} class="status-row" tabIndex={0} onClick={() => (statusViewer.value = { owner: x.owner, index: Math.max(0, x.list.findIndex((s) => !s.viewed)) })}>
      <StatusRing jid={x.owner} name={S.displayName(x.owner)} url={S.profiles.value.get(x.owner)?.avatar_url} list={x.list} />
      <span class="grow">
        <span class="chat-name">{S.displayName(x.owner)}</span>
        <small class="muted">{ago(x.list[x.list.length - 1].createdAt)}</small>
      </span>
    </li>
  );

  return (
    <div class="tab-body">
      <ul class="status-list">
        <li class="status-row mine" tabIndex={0} onClick={() => (mine.length ? (statusViewer.value = { owner: me, index: 0 }) : file.current?.click())}>
          {mine.length ? (
            <StatusRing jid={me} name={myProfile?.name || "You"} url={myProfile?.avatar_url} list={mine} />
          ) : (
            <span class="status-ring add">
              <Avatar url={myProfile?.avatar_url} jid={me} name={myProfile?.name || "You"} size={48} />
              <span class="status-plus">+</span>
            </span>
          )}
          <span class="grow">
            <span class="chat-name">My status</span>
            <small class="muted">{mine.length ? `${mine.length} update${mine.length === 1 ? "" : "s"} · ${ago(mine[mine.length - 1].createdAt)}` : "Tap to add status update"}</small>
          </span>
        </li>
        <li class="status-actions">
          <button class="chip" onClick={() => (statusComposer.value = "text")} aria-label="New text status">
            <Icon name="edit" size={16} /> Text
          </button>
          <button class="chip" onClick={() => file.current?.click()} aria-label="New photo or video status">
            <Icon name="camera" size={16} /> Photo or video
          </button>
          <button class="chip" onClick={() => (drawer.value = "statusPrivacy")} aria-label="Status privacy">
            <Icon name="lock" size={16} /> Privacy
          </button>
          <input
            ref={file}
            type="file"
            accept="image/*,video/*"
            hidden
            aria-label="Status photo or video"
            onChange={(e) => {
              const f = e.currentTarget.files?.[0];
              e.currentTarget.value = "";
              if (f) statusComposer.value = f;
            }}
          />
        </li>
        {recent.length > 0 && <li class="list-heading">Recent updates</li>}
        {recent.map(row)}
        {viewed.length > 0 && <li class="list-heading">Viewed updates</li>}
        {viewed.map(row)}
        {!others.length && <li class="muted pad small">Status updates from your contacts appear here for 24 hours. You both need to have each other's number saved.</li>}
      </ul>
      <p class="muted small pad center">
        <Icon name="lock" size={12} /> Your status updates are end-to-end encrypted.
      </p>
    </div>
  );
}

// ---- composer ------------------------------------------------------------------------------

let composerSeq = 0;
const composerKeys = new WeakMap<File, number>();

/** Fresh composer state for every new status. */
export function StatusComposer() {
  const what = statusComposer.value;
  if (!what) return null;
  if (what !== "text" && !composerKeys.has(what)) composerKeys.set(what, ++composerSeq);
  return <ComposerInner key={what === "text" ? "text" + composerSeq : "file" + composerKeys.get(what)} what={what} />;
}

function ComposerInner({ what }: { what: "text" | File }) {
  const [text, setText] = useState("");
  const [bg, setBg] = useState(STATUS_BGS[0]);
  const [font, setFont] = useState(0);
  const [caption, setCaption] = useState("");
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mentions, setMentions] = useState<string[]>([]);
  const [mentioning, setMentioning] = useState(false);
  const close = () => (statusComposer.value = null);
  useEscape(close);
  useEffect(() => {
    if (!(what instanceof File)) return;
    const u = URL.createObjectURL(what);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [what]);
  if (!what) return null;

  async function send() {
    setBusy(true);
    try {
      const e = engine.value!;
      if (what === "text") await e.postStatus({ kind: "text", text: text.trim(), bg, font, mentions });
      else {
        const p = await prepareFile(what as File);
        if (p.kind !== "image" && p.kind !== "video") throw new Error("Status updates can be photos or videos");
        await e.postStatus({ kind: p.kind, file: p.media.blob!, caption: caption.trim(), width: p.media.width, height: p.media.height, duration: p.media.duration, thumb: p.media.thumb, mentions });
      }
      toast("Status update sent");
      if (what === "text") composerSeq++;
      close();
    } catch (err) {
      toast(errText(err));
      setBusy(false);
    }
  }

  if (what === "text")
    return (
      <div class="status-compose" style={{ background: bg }} role="dialog" aria-label="Text status">
        <header class="status-compose-bar">
          <button class="icon-btn light" onClick={close} aria-label="Close">
            <Icon name="close" />
          </button>
          <span class="grow" />
          <button class="icon-btn light" onClick={() => setMentioning(true)} aria-label="Mention">
            <Icon name="at" />
          </button>
          <button class="icon-btn light font-btn" onClick={() => setFont((font + 1) % FONTS.length)} aria-label="Font" style={{ fontFamily: FONTS[(font + 1) % FONTS.length] }}>
            T
          </button>
          <button class="icon-btn light" onClick={() => setBg(STATUS_BGS[(STATUS_BGS.indexOf(bg) + 1) % STATUS_BGS.length])} aria-label="Color">
            <span class="color-dot" />
          </button>
        </header>
        <textarea
          class="status-text-input"
          style={{ fontFamily: FONTS[font] }}
          value={text}
          maxLength={700}
          autoFocus
          placeholder="Type a status"
          aria-label="Status text"
          onInput={(e) => setText(e.currentTarget.value)}
        />
        {mentions.length > 0 && (
          <div class="status-mention-chips">
            {mentions.map((j) => (
              <button key={j} class="chip" onClick={() => setMentions(mentions.filter((x) => x !== j))} aria-label={`Don't mention ${S.displayName(j)}`}>
                @{S.displayName(j)} <Icon name="close" size={14} />
              </button>
            ))}
          </div>
        )}
        {mentioning && <MentionDialog picked={mentions} onDone={(l) => (setMentions(l), setMentioning(false))} />}
        {text.trim() && (
          <button class="icon-btn send-fab status-send" disabled={busy} onClick={() => void send()} aria-label="Send status">
            <Icon name="send" />
          </button>
        )}
      </div>
    );

  const isVideo = (what as File).type.startsWith("video/");
  return (
    <div class="status-compose media" role="dialog" aria-label="Photo or video status">
      <header class="status-compose-bar">
        <button class="icon-btn light" onClick={close} aria-label="Close">
          <Icon name="close" />
        </button>
        <span class="grow" />
        <button class="icon-btn light" onClick={() => setMentioning(true)} aria-label="Mention">
          <Icon name="at" />
        </button>
      </header>
      {mentions.length > 0 && (
        <div class="status-mention-chips">
          {mentions.map((j) => (
            <button key={j} class="chip" onClick={() => setMentions(mentions.filter((x) => x !== j))} aria-label={`Don't mention ${S.displayName(j)}`}>
              @{S.displayName(j)} <Icon name="close" size={14} />
            </button>
          ))}
        </div>
      )}
      {mentioning && <MentionDialog picked={mentions} onDone={(l) => (setMentions(l), setMentioning(false))} />}
      <div class="status-media-preview">{preview && (isVideo ? <video src={preview} controls playsInline /> : <img src={preview} alt="" />)}</div>
      <div class="status-caption">
        <input class="line-input" value={caption} maxLength={700} placeholder="Add a caption…" aria-label="Caption" onInput={(e) => setCaption(e.currentTarget.value)} />
        <button class="icon-btn send-fab" disabled={busy} onClick={() => void send()} aria-label="Send status">
          <Icon name="send" />
        </button>
      </div>
    </div>
  );
}

/** Pick who to @mention: only people who'll see the status. */
function MentionDialog({ picked, onDone }: { picked: string[]; onDone: (list: string[]) => void }) {
  const [list, setList] = useState(picked);
  const [audience, setAudience] = useState<string[] | null>(null);
  useEffect(() => void api.statusAudience().then((a) => setAudience(a.audience), () => setAudience([])), []);
  const exclude = audience ? S.contacts.value.map((c) => c.jid).filter((j) => !audience.includes(j)) : [];
  return (
    <div class="modal-backdrop" onClick={() => onDone(list)}>
      <div class="modal mention-dialog" role="dialog" aria-label="Mention" onClick={(e) => e.stopPropagation()}>
        <h2>Mention</h2>
        <p class="muted small">They'll get a message about it and can reshare it. Only people who can see your status can be mentioned.</p>
        {audience ? <ContactPicker exclude={exclude} picked={list} onChange={setList} /> : <p class="muted">Loading…</p>}
        <div class="row-buttons">
          <button class="primary" onClick={() => onDone(list)}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/** In a chat: "Mentioned you in their status" with View and Reshare. */
export function StatusMention({ m }: { m: Msg }) {
  const ref = m.statusRef!;
  const me = S.myJid.value;
  const owner = m.dir === "in" ? m.from : me;
  const live = (owner === me ? S.myStatuses.value : (S.statuses.value.get(owner) ?? [])).filter((s) => s.expiresAt > Date.now());
  const index = live.findIndex((s) => s.id === ref.id);
  const v = live[index];
  const [busy, setBusy] = useState(false);
  const reshare = () => {
    setBusy(true);
    void engine.value!.reshareStatus(v).then(
      () => toast("Added to your status"),
      (e) => (toast(errText(e)), setBusy(false)),
    );
  };
  return (
    <div class="status-mention-card">
      <span class="status-mention-icon">
        <Icon name="status" />
      </span>
      <span class="grow">
        <strong>{m.dir === "in" ? "Mentioned you in their status" : `You mentioned ${S.displayName(m.chat)} in your status`}</strong>
        {ref.text && <small class="muted ellipsis">{ref.text}</small>}
      </span>
      <span class="status-mention-actions">
        {v ? (
          <button class="chip" onClick={() => (statusViewer.value = { owner, index })}>
            View
          </button>
        ) : (
          <small class="muted">No longer available</small>
        )}
        {v && m.dir === "in" && (
          <button class="chip on" disabled={busy} onClick={reshare}>
            Reshare
          </button>
        )}
      </span>
    </div>
  );
}

// ---- viewer ---------------------------------------------------------------------------------

const PHOTO_MS = 5000;

export function StatusViewer() {
  const sel = statusViewer.value;
  const me = S.myJid.value;
  const list = sel ? (sel.owner === me ? S.myStatuses.value : (S.statuses.value.get(sel.owner) ?? [])).filter((s) => s.expiresAt > Date.now()) : [];
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reply, setReply] = useState("");
  const [typing, setTyping] = useState(false);
  const [viewers, setViewers] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [duration, setDuration] = useState(PHOTO_MS);
  const index = sel ? Math.min(sel.index, list.length - 1) : 0;
  const cur = list[index];
  const own = sel?.owner === me;
  const close = () => (statusViewer.value = null);
  const go = (d: number) => {
    if (!sel) return;
    const next = index + d;
    if (next < 0) return setProgress(0);
    if (next >= list.length) return close();
    statusViewer.value = { ...sel, index: next };
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === "INPUT") return;
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Mark seen; load (decrypt) media.
  useEffect(() => {
    if (!cur) return;
    setProgress(0);
    setSrc(null);
    setDuration(cur.kind === "video" ? Math.max(3, Math.min(30, cur.media?.duration ?? 15)) * 1000 : PHOTO_MS);
    void engine.value?.viewStatus(cur);
    if (cur.media) engine.value?.statusMediaURL(cur).then(setSrc, (e) => toast(errText(e)));
  }, [cur?.id]);

  const hold = paused || !!reply || typing || viewers || confirmDelete || (cur?.kind !== "text" && !src);
  useEffect(() => {
    if (!cur || hold) return;
    const step = 50;
    const t = setInterval(() => setProgress((p) => p + step / duration), step);
    return () => clearInterval(t);
  }, [cur?.id, hold, duration]);
  useEffect(() => {
    if (progress >= 1) go(1);
  }, [progress]);

  if (!sel || !cur) return null;
  const name = own ? "My status" : S.displayName(sel.owner);
  const views = cur.views ?? [];
  const likes = views.filter((v) => v.reaction).length;
  const react = (emoji: string) =>
    void engine.value?.reactStatus(cur, emoji).then(
      () => emoji && emoji !== "❤️" && toast(`Reacted ${emoji}`),
      (e) => toast(errText(e)),
    );

  return (
    <div class="status-viewer" role="dialog" aria-label={`Status from ${name}`}>
      <div class="status-bars">
        {list.map((s, i) => (
          <span key={s.id} class="status-bar">
            <span style={{ width: `${i < index ? 100 : i === index ? Math.min(100, progress * 100) : 0}%` }} />
          </span>
        ))}
      </div>
      <header class="status-head">
        <button class="icon-btn light" onClick={close} aria-label="Close status">
          <Icon name="back" />
        </button>
        <Avatar url={S.profiles.value.get(sel.owner)?.avatar_url} jid={sel.owner} name={name} size={40} />
        <span class="grow">
          <strong>{name}</strong>
          <small>{ago(cur.createdAt)}</small>
        </span>
        <button class="icon-btn light" onClick={() => setPaused(!paused)} aria-label={paused ? "Play" : "Pause"}>
          <Icon name={paused ? "play" : "pause"} />
        </button>
        {own && (
          <button class="icon-btn light" onClick={() => setConfirmDelete(true)} aria-label="Delete status">
            <Icon name="trash" />
          </button>
        )}
      </header>
      <div class="status-stage" onPointerDown={() => setPaused(true)} onPointerUp={() => setPaused(false)}>
        <button class="status-nav prev" onClick={() => go(-1)} aria-label="Previous status" />
        {cur.kind === "text" ? (
          <div class="status-text" style={{ background: cur.bg, fontFamily: FONTS[cur.font ?? 0] }}>
            {cur.text}
          </div>
        ) : cur.kind === "image" ? (
          src ? <img class="status-img" src={src} alt={cur.caption || "Status photo"} /> : <img class="status-img blur" src={cur.media?.thumb} alt="" />
        ) : src ? (
          <video class="status-img" src={src} autoPlay playsInline muted={false} onLoadedMetadata={(e) => setDuration(Math.min(30, e.currentTarget.duration || 15) * 1000)} />
        ) : (
          <img class="status-img blur" src={cur.media?.thumb} alt="" />
        )}
        <button class="status-nav next" onClick={() => go(1)} aria-label="Next status" />
        {cur.caption && <div class="status-caption-text">{cur.caption}</div>}
        {cur.mentions && (
          <div class="status-mentions">
            {cur.mentions.map((j) => (
              <span key={j} class="status-mention">
                @{j === me ? "You" : S.displayName(j)}
              </span>
            ))}
          </div>
        )}
      </div>
      <footer class="status-foot">
        {own ? (
          <button class="status-views-btn" onClick={() => setViewers(true)} aria-label="Viewers">
            <Icon name="info" size={18} /> {views.length} view{views.length === 1 ? "" : "s"}
            {likes > 0 && (
              <span class="status-likes">
                <Icon name="heart" size={16} /> {likes}
              </span>
            )}
          </button>
        ) : (
          <form
            class="status-reply"
            onSubmit={(e) => {
              e.preventDefault();
              const t = reply.trim();
              if (!t) return;
              void engine.value?.replyToStatus(cur, t).then(() => toast("Reply sent"));
              setReply("");
            }}
          >
            {typing && !reply && (
              <div class="status-quick" onPointerDown={(e) => e.preventDefault()}>
                {QUICK_REACTIONS.map((r) => (
                  <button key={r} type="button" class={cur.reaction === r ? "on" : ""} onClick={() => react(cur.reaction === r ? "" : r)} aria-label={`React ${r}`}>
                    {r}
                  </button>
                ))}
              </div>
            )}
            <input
              class="line-input"
              value={reply}
              onInput={(e) => setReply(e.currentTarget.value)}
              onFocus={() => setTyping(true)}
              onBlur={() => setTyping(false)}
              placeholder="Reply…"
              aria-label="Reply to status"
            />
            {reply.trim() ? (
              <button class="icon-btn send-fab" aria-label="Send reply">
                <Icon name="send" />
              </button>
            ) : (
              <button type="button" class={"icon-btn light status-like" + (cur.reaction === "❤️" ? " on" : "")} onClick={() => react(cur.reaction === "❤️" ? "" : "❤️")} aria-label={cur.reaction === "❤️" ? "Unlike status" : "Like status"} aria-pressed={cur.reaction === "❤️"}>
                <Icon name={cur.reaction === "❤️" ? "heart" : "heartOutline"} />
              </button>
            )}
          </form>
        )}
      </footer>
      {viewers && (
        <div class="modal-backdrop" onClick={() => setViewers(false)}>
          <div class="modal status-viewers" role="dialog" aria-label="Viewed by" onClick={(e) => e.stopPropagation()}>
            <h2>Viewed by {views.length}</h2>
            {!S.privacy.value.read_receipts && <p class="muted small">Read receipts are off, so you can't see who viewed your status (only who reacted).</p>}
            <ul class="plain-list">
              {views
                .filter((v) => S.privacy.value.read_receipts || v.reaction)
                .map((v) => (
                  <li key={v.jid}>
                    <Avatar url={S.profiles.value.get(v.jid)?.avatar_url} jid={v.jid} name={S.displayName(v.jid)} size={36} />
                    <span class="grow">{S.displayName(v.jid)}</span>
                    {v.reaction && (
                      <span class="viewer-reaction" aria-label={`Reacted ${v.reaction}`}>
                        {v.reaction}
                      </span>
                    )}
                    <small class="muted">{listTime(v.at)}</small>
                  </li>
                ))}
            </ul>
            <div class="row-buttons">
              <button class="primary" onClick={() => setViewers(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDelete && (
        <Confirm
          text="Delete this status update? It will also be deleted for everyone who received it."
          action="Delete"
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            setConfirmDelete(false);
            void engine.value?.deleteStatus(cur.id).then(() => list.length <= 1 && close(), (e) => toast(errText(e)));
          }}
        />
      )}
    </div>
  );
}

// ---- status privacy ---------------------------------------------------------------------------

export function StatusPrivacyPanel() {
  const cur = S.statusPrivacy.value;
  const [audience, setAudience] = useState(cur.audience);
  const [list, setList] = useState<string[]>(cur.list);
  const [busy, setBusy] = useState(false);
  const back = () => (drawer.value = "none");
  useEscape(back);
  async function save() {
    setBusy(true);
    try {
      await engine.value!.setStatusPrivacy(audience, audience === "contacts" ? [] : list);
      toast("Status privacy saved");
      back();
    } catch (e) {
      toast(errText(e));
      setBusy(false);
    }
  }
  const opt = (v: typeof audience, label: string) => (
    <label class="radio-row">
      <input type="radio" name="status-audience" checked={audience === v} onChange={() => setAudience(v)} />
      {label}
    </label>
  );
  return (
    <div class="drawer" role="dialog" aria-label="Status privacy">
      <header class="drawer-head">
        <button class="icon-btn" onClick={back} aria-label="Back">
          <Icon name="back" />
        </button>
        <h2>Status privacy</h2>
      </header>
      <div class="drawer-body">
        <p class="muted pad small">Who can see my status updates</p>
        <div class="pad">
          {opt("contacts", "My contacts")}
          {opt("except", "My contacts except…")}
          {opt("only", "Only share with…")}
        </div>
        {audience !== "contacts" && <ContactPicker picked={list} onChange={setList} />}
        <p class="muted pad small">Changes won't affect status updates you've already sent. Only contacts who also saved your number see your status.</p>
        <div class="pad">
          <button class="primary" disabled={busy} onClick={() => void save()}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Channels tab ------------------------------------------------------------------------------

export function ChannelsTab() {
  const chans = [...S.channels.value.values()];
  const byJid = new Map(S.chats.value.map((c) => [c.jid, c]));
  chans.sort((a, b) => (byJid.get(b.jid)?.lastTs ?? 0) - (byJid.get(a.jid)?.lastTs ?? 0));
  const active = S.activeChat.value;
  return (
    <div class="tab-body">
      <div class="status-actions pad-x">
        <button class="chip" onClick={() => (drawer.value = "channelDirectory")}>
          <Icon name="search" size={16} /> Find channels
        </button>
        <button class="chip" onClick={() => (drawer.value = "newChannel")}>
          <Icon name="plus" size={16} /> Create channel
        </button>
      </div>
      <ul class="chat-list" aria-label="Channels">
        {chans.map((c) => {
          const chat = byJid.get(c.jid);
          return (
            <li
              key={c.jid}
              class={"chat-row" + (active === c.jid ? " active" : "")}
              tabIndex={0}
              onClick={() => openChat(c.jid)}
              onKeyDown={(e) => e.key === "Enter" && openChat(c.jid)}
            >
              <Avatar url={c.avatar_url} jid={c.jid} name={c.name} size={49} group />
              <div class="chat-row-main">
                <div class="chat-row-top">
                  <span class="chat-name">{c.name}</span>
                  <span class={"chat-time" + (chat?.unread ? " unread" : "")}>{chat?.lastTs ? listTime(chat.lastTs) : ""}</span>
                </div>
                <div class="chat-row-bottom">
                  <span class="chat-last">{chat?.lastBody || (c.my_role === "follower" ? `${c.followers} followers` : "No posts yet")}</span>
                  {!!chat?.unread && <span class="badge muted-badge">{chat.unread}</span>}
                </div>
              </div>
            </li>
          );
        })}
        {!chans.length && <li class="muted pad small">Follow channels to get updates on topics you care about. Channels are public; your phone number isn't shown to admins or other followers.</li>}
      </ul>
    </div>
  );
}

export function ChannelDirectory() {
  const [q, setQ] = useState("");
  const [list, setList] = useState<Channel[] | null>(null);
  const back = () => (drawer.value = "none");
  useEscape(back);
  useEffect(() => {
    const t = setTimeout(() => void api.channelDirectory(q.trim()).then((r) => setList(r.channels), (e) => toast(errText(e))), 200);
    return () => clearTimeout(t);
  }, [q]);
  const [busy, setBusy] = useState<string | null>(null);
  async function follow(c: Channel) {
    setBusy(c.jid);
    try {
      const n = await engine.value!.follow(c.jid, true);
      setList((l) => l?.map((x) => (x.jid === c.jid ? n : x)) ?? null);
    } catch (e) {
      toast(errText(e));
    } finally {
      setBusy(null);
    }
  }
  return (
    <div class="drawer" role="dialog" aria-label="Find channels">
      <header class="drawer-head">
        <button class="icon-btn" onClick={back} aria-label="Back">
          <Icon name="back" />
        </button>
        <h2>Find channels</h2>
      </header>
      <div class="drawer-body">
        <div class="search">
          <Icon name="search" size={20} />
          <input value={q} onInput={(e) => setQ(e.currentTarget.value)} placeholder="Search channels" aria-label="Search channels" autoFocus />
        </div>
        <ul class="plain-list channel-dir">
          {list?.map((c) => (
            <li key={c.jid}>
              <button
                class="channel-dir-who"
                onClick={() => {
                  back();
                  void engine.value?.openChat(c.jid);
                }}
              >
                <Avatar url={c.avatar_url} jid={c.jid} name={c.name} size={44} group />
                <span class="grow">
                  <span class="chat-name">{c.name}</span>
                  <small class="muted">{c.followers.toLocaleString()} follower{c.followers === 1 ? "" : "s"}</small>
                </span>
              </button>
              {c.my_role ? (
                <span class="muted small">{c.my_role === "follower" ? "Following" : "Admin"}</span>
              ) : (
                <button class="chip on" disabled={busy === c.jid} onClick={() => void follow(c)} aria-label={"Follow " + c.name}>
                  Follow
                </button>
              )}
            </li>
          ))}
          {list && !list.length && <li class="muted pad">No channels found.</li>}
        </ul>
      </div>
    </div>
  );
}

export function NewChannel() {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState<{ id: string; url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const back = () => (drawer.value = "none");
  useEscape(back);
  async function create() {
    setBusy(true);
    try {
      const c = await engine.value!.createChannel(name.trim(), description.trim(), icon?.id);
      back();
      S.tab.value = "channels";
      await engine.value!.openChat(c.jid);
    } catch (e) {
      toast(errText(e));
      setBusy(false);
    }
  }
  return (
    <div class="drawer" role="dialog" aria-label="New channel">
      <header class="drawer-head">
        <button class="icon-btn" onClick={back} aria-label="Back">
          <Icon name="back" />
        </button>
        <h2>New channel</h2>
      </header>
      <div class="drawer-body">
        <div class="new-group-details">
          <label class="group-icon-pick">
            <Avatar url={icon?.url} jid="new-channel" name={name} size={140} group />
            <span class="avatar-picker-overlay">
              <Icon name="camera" size={24} />
              Add channel icon
            </span>
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.currentTarget.files?.[0];
                if (f) void uploadAvatar(f).then((u) => setIcon({ id: u.id, url: u.url }), (err) => toast(errText(err)));
              }}
            />
          </label>
          <input class="line-input" value={name} maxLength={100} onInput={(e) => setName(e.currentTarget.value)} placeholder="Channel name" aria-label="Channel name" autoFocus />
          <textarea
            class="line-input"
            value={description}
            maxLength={2048}
            rows={3}
            onInput={(e) => setDescription(e.currentTarget.value)}
            placeholder="Describe your channel (optional)"
            aria-label="Channel description"
          />
          <p class="muted small">Channels are public: anyone can find and follow them. Your phone number isn't shown to followers.</p>
          <button class="primary" disabled={!name.trim() || busy} onClick={() => void create()}>
            Create channel
          </button>
        </div>
      </div>
    </div>
  );
}

/** Chat-bar line for a channel. */
export function ChannelStatusLine({ jid }: { jid: string }) {
  const c = S.channels.value.get(jid) ?? preview.value.get(jid);
  return <span class="status">{c ? `Channel · ${c.followers.toLocaleString()} follower${c.followers === 1 ? "" : "s"}` : "Channel"}</span>;
}

/** Channels opened without following (preview). */
export const preview = signal(new Map<string, Channel>());

/** Loads info + recent posts when a channel is opened. */
export function useChannel(jid: string) {
  useEffect(() => {
    const e = engine.value;
    if (!e || !jid) return;
    void e.loadChannel(jid).then((c) => {
      if (c && !c.my_role) preview.value = new Map(preview.value).set(jid, c);
    });
    void e.backfillChannel(jid);
  }, [jid]);
}

/** Footer instead of a composer: followers can't post. */
export function ChannelFooter({ jid }: { jid: string }) {
  const c = S.channels.value.get(jid);
  const [busy, setBusy] = useState(false);
  if (c?.my_role === "owner" || c?.my_role === "admin") return null;
  if (!c)
    return (
      <footer class="composer-blocked">
        <button
          class="primary"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void engine.value!.follow(jid, true).then(
              () => setBusy(false),
              (e) => (toast(errText(e)), setBusy(false)),
            );
          }}
        >
          Follow
        </button>
      </footer>
    );
  return <footer class="composer-blocked">Only admins can post in this channel. React to posts to let them know what you think.</footer>;
}

export function ChannelInfo({ jid }: { jid: string }) {
  const c = S.channels.value.get(jid) ?? preview.value.get(jid);
  const e = engine.value!;
  const [confirm, setConfirm] = useState<"unfollow" | "delete" | null>(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(c?.name ?? "");
  const [desc, setDesc] = useState(c?.description ?? "");
  const admin = c?.my_role === "owner" || c?.my_role === "admin";
  if (!c) return null;
  const link = `${serverOrigin()}/?channel=${encodeURIComponent(jid)}`;
  return (
    <aside class="info" aria-label="Channel info">
      <header class="bar">
        <button class="icon-btn" onClick={() => (infoOpen.value = false)} aria-label="Close">
          <Icon name="close" />
        </button>
        <h2>Channel info</h2>
      </header>
      <div class="info-body">
        <div class="info-hero">
          <Avatar url={c.avatar_url} jid={jid} name={c.name} size={160} group />
          {editing ? (
            <div class="edit-channel">
              <input class="line-input" value={name} maxLength={100} onInput={(ev) => setName(ev.currentTarget.value)} aria-label="Channel name" />
              <textarea class="line-input" value={desc} rows={3} maxLength={2048} onInput={(ev) => setDesc(ev.currentTarget.value)} aria-label="Channel description" />
              <div class="row-buttons">
                <button class="link" onClick={() => setEditing(false)}>
                  Cancel
                </button>
                <button
                  class="primary"
                  disabled={!name.trim()}
                  onClick={() =>
                    void api.patchChannel(jid, { name: name.trim(), description: desc.trim() }).then(
                      (n) => ((S.channels.value = new Map(S.channels.value).set(jid, n)), setEditing(false)),
                      (err) => toast(errText(err)),
                    )
                  }
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <>
              <h3>{c.name}</h3>
              <p class="muted">
                Channel · {c.followers.toLocaleString()} follower{c.followers === 1 ? "" : "s"}
              </p>
            </>
          )}
        </div>
        {!editing && c.description && <p class="info-about pad">{c.description}</p>}
        <div class="info-actions">
          <button class="list-action" onClick={() => navigator.clipboard?.writeText(link).then(() => toast("Channel link copied"))}>
            <span class="list-action-icon">
              <Icon name="link" />
            </span>
            Copy channel link
          </button>
          {admin && !editing && (
            <button class="list-action" onClick={() => setEditing(true)}>
              <span class="list-action-icon">
                <Icon name="edit" />
              </span>
              Edit channel info
            </button>
          )}
          {c.my_role === "follower" && (
            <button class="list-action danger" onClick={() => setConfirm("unfollow")}>
              <span class="list-action-icon">
                <Icon name="exit" />
              </span>
              Unfollow channel
            </button>
          )}
          {!c.my_role && (
            <button class="list-action" onClick={() => void e.follow(jid, true).catch((err) => toast(errText(err)))}>
              <span class="list-action-icon">
                <Icon name="plus" />
              </span>
              Follow channel
            </button>
          )}
          {c.my_role === "owner" && (
            <button class="list-action danger" onClick={() => setConfirm("delete")}>
              <span class="list-action-icon">
                <Icon name="trash" />
              </span>
              Delete channel
            </button>
          )}
        </div>
        <p class="muted small pad">
          <Icon name="lock" size={12} /> Channels are public and not end-to-end encrypted. Admins and other followers can't see your phone number; reactions are
          counted, not listed.
        </p>
      </div>
      {confirm && (
        <Confirm
          text={confirm === "delete" ? `Delete "${c.name}"? Followers will no longer be able to see it.` : `Unfollow "${c.name}"?`}
          action={confirm === "delete" ? "Delete" : "Unfollow"}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            setConfirm(null);
            infoOpen.value = false;
            void (confirm === "delete" ? e.deleteChannel(jid) : e.follow(jid, false)).catch((err) => toast(errText(err)));
          }}
        />
      )}
    </aside>
  );
}
